import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { TError } from 'api/client';
import type { TDisableJourney } from 'api/models/TAppConfig';

export type ConfigEntry = {
  data?: undefined | TDisableJourney;
  errors?: TError[] | undefined;
  isLoading?: boolean | undefined;
};

export type SettingsState = {
  showFuelSelection: boolean;
  isActionUpsell: boolean;
  isMixedMeterJourney: boolean;
  config: Record<string, ConfigEntry>;
};

type SetIsLoadingPayload = {
  configId: string;
};

type SetSuccessPayload = {
  configId: string;
  data: TDisableJourney;
};

type SetErrorPayload = {
  configId: string;
  errors: TError[];
};

export const initialState: SettingsState = {
  showFuelSelection: true,
  isActionUpsell: false,
  isMixedMeterJourney: false,
  config: {}
};

export const slice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    setInitialState: () => initialState,
    setShowFuelSelection: (state, { payload }: PayloadAction<boolean>) => {
      state.showFuelSelection = payload;
    },
    setIsActionUpsell: (state, { payload }: PayloadAction<boolean>) => {
      state.isActionUpsell = payload;
    },
    setIsMixedMeterJourney: (state, { payload }: PayloadAction<boolean>) => {
      state.isMixedMeterJourney = payload;
    },
    setIsLoading: (state, { payload }: PayloadAction<SetIsLoadingPayload>) => {
      const { configId } = payload;
      state.config[configId] = {};
      state.config[configId].isLoading = true;
      state.config[configId].errors = initialState.config[configId]?.errors;
    },
    setSuccess: (state, { payload }: PayloadAction<SetSuccessPayload>) => {
      const { configId, data } = payload;
      state.config[configId] = {};
      state.config[configId].isLoading = false;
      state.config[configId].data = data;
      state.config[configId].errors = initialState.config[configId]?.errors;
    },
    setError: (state, { payload }: PayloadAction<SetErrorPayload>) => {
      const { configId, errors } = payload;
      state.config[configId] = {};
      state.config[configId].isLoading = false;
      state.config[configId].data = initialState.config[configId]?.data;
      state.config[configId].errors = errors;
    }
  }
});

export const {
  setInitialState,
  setShowFuelSelection,
  setIsActionUpsell,
  setIsMixedMeterJourney,
  setIsLoading,
  setSuccess,
  setError
} = slice.actions;

export const selectSlice = (state) => state[slice.name];
export const selectShowFuelSelection = (state) =>
  selectSlice(state).showFuelSelection;
export const selectIsActionUpsell = (state) =>
  selectSlice(state).isActionUpsell;
export const selectIsMixedMeterJourney = (state) =>
  selectSlice(state).isMixedMeterJourney;

export const selectConfig = (state) => selectSlice(state).config;

// by id
export const selectConfigById = (state, configId) =>
  selectConfig(state)[configId] || {};
export const selectDataById = (state, configId) =>
  selectConfigById(state, configId).data;
export const selectIsLoadingById = (state, configId) =>
  selectConfigById(state, configId).isLoading;
export const selectErrorsById = (state, configId) =>
  selectConfigById(state, configId).errors;

export default slice.reducer;
